import React, { Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IoMdEye } from 'react-icons/io';
import PageList from '../../templates/PageList';
import SelectFilter from '../../organisms/SelectFilter';
import PageSearch from '../../molecules/PageSearch';
import { useSignal } from '@preact/signals-react';
import appConfig from '../../../config/app-config';

const RecommendationEngineRecommendationsList: React.FC = () => {
    const navigate = useNavigate();
    const { page: pageParam, pageSize: pageSizeParam, field: fieldSorted, order: orderSorted }: any = useParams();
    const page = parseInt(pageParam) || 0;
    const pageSize = parseInt(pageSizeParam) || 20;

    const { address: DATA_PROVIDER_URL, key: DATA_PROVIDER_KEY } = appConfig.apis.dataProviderApiEnv;

    const providers = useSignal<string[]>([]);
    const [provider, setProvider] = React.useState('');
    const [status, setStatus] = React.useState('');
    const [identification, setIdentification] = React.useState('');

    const BASE_URL = `${DATA_PROVIDER_URL}/batch-reports/page-list`;

    // Construir URL com filtros
    const buildUrl = () => {
        let url = BASE_URL;
        const params = [];

        // Adicionar paginação
        params.push(`page=${page}`);
        params.push(`pageSize=${pageSize}`);

        // Ordenação padrão por data decrescente
        params.push(`sort=createdAt`);
        params.push(`order=DESC`);

        // Adicionar filtros específicos se selecionados
        if (provider) params.push(`provider=${provider}`);
        if (status) params.push(`status=${status}`);
        if (identification) params.push(`identification=${identification}`);
        params.push('excludePrefix=CAE-');

        return url + '?' + params.join('&');
    };

    const renderRows = (rows: any[]) => {
        if (!rows || rows.length === 0) {
            return (
                <tr>
                    <td colSpan={6} className="py-2 px-2 text-center">
                        Nenhum lote encontrado
                    </td>
                </tr>
            );
        }

        const filtered = rows;

        if (filtered.length === 0) {
            return (
                <tr>
                    <td colSpan={6} className="py-2 px-2 text-center">
                        Nenhum lote encontrado
                    </td>
                </tr>
            );
        }

        return filtered.map((batch: any) => (
            <tr
                key={batch.id}
                className="hover:bg-gray-50 dark:hover:bg-slate-600 cursor-pointer"
                onClick={() => navigate(`/recommendation-engine/recommendations/${batch.sk}`)}
            >
                <td className="px-6 py-4 text-gray-900 dark:text-gray-100 text-center">
                    {batch.identification || batch.id}
                </td>
                <td className="px-6 py-4 text-gray-900 dark:text-gray-100 text-center">
                    {batch.provider}
                </td>
                <td className="px-6 py-4 text-gray-900 dark:text-gray-100 text-center">
                    {batch.documentsCount || 0}
                </td>
                <td className="px-6 py-4 text-gray-900 dark:text-gray-100 text-center">
                    {formatDate(batch.createdAt)}
                </td>
                <td className="px-6 py-4 text-center">
                    <span className={getStatusBadge(batch.status)}>
                        {getStatusLabel(batch.status)}
                    </span>
                </td>
                <td className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                        <button
                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded flex items-center justify-center"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/recommendation-engine/recommendations/${batch.sk}`);
                            }}
                            title="Ver Detalhes"
                        >
                            <IoMdEye className="w-4 h-4" />
                        </button>
                    </div>
                </td>
            </tr>
        ));
    };

    const getStatusBadge = (status: string) => {
        const colors = {
            'PENDING': 'px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
            'PROCESSING': 'px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
            'COMPLETED': 'px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
            'ERROR': 'px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
        };
        return (colors as any)[status] || 'px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    };

    const getStatusLabel = (status: string) => {
        const labels = {
            'PENDING': 'Pendente',
            'PROCESSING': 'Processando',
            'COMPLETED': 'Completo',
            'ERROR': 'Erro'
        };
        return (labels as any)[status] || status;
    };

    const pageChanged = (newPage: number) => {
        navigate(`/recommendation-engine/recommendations/${newPage}/${pageSize}/${fieldSorted}/${orderSorted}`);
    };

    const pageSizeChanged = (newPageSize: number) => {
        navigate(`/recommendation-engine/recommendations/0/${newPageSize}/${fieldSorted}/${orderSorted}`);
    };

    const pageSorted = (field: string, order: string) => {
        navigate(`/recommendation-engine/recommendations/${page}/${pageSize}/${field}/${order}`);
    };

    const providerFilterChanged = (value: string) => {
        setProvider(value);
    };

    const statusFilterChanged = (value: string) => {
        setStatus(value);
    };

    const identificationFilterChanged = (value: string) => {
        setIdentification(value);
    };

    const clearFilters = () => {
        setProvider('');
        setStatus('');
        setIdentification('');
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return 'N/A';

        // Se já está no formato brasileiro (dd/mm/yyyy hh:mm)
        if (dateStr.includes('/')) {
            return dateStr;
        }

        // Se está no formato ISO
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        } catch {
            return 'N/A';
        }
    };

    // Pegar token JWT
    const authToken = localStorage.getItem('authToken');

    return (
        <Fragment>
            <PageList
                key={`${provider}-${status}-${identification}`}
                pageTitle="Recomendações"
                url={buildUrl()}
                fetchHeaders={[
                    { key: 'x-api-key', value: DATA_PROVIDER_KEY },
                    { key: 'Authorization', value: `Bearer ${authToken}` }
                ]}
                breadcrumb={[{ label: 'Recomendações', url: '#' }]}
                tableFieldSort={fieldSorted}
                tableOrderSort={orderSorted}
                onTableSort={pageSorted}
                renderRows={renderRows}
                page={page}
                pageSize={pageSize}
                onPageChange={pageChanged}
                onItemsPerPageChange={pageSizeChanged}
                itemsLabel="lotes"
                defaultBackendHeader={false}
                tableHeaders={[
                    { id: 'identification', title: 'Identificação', field: 'identification', width: '200px', canSort: false },
                    { id: 'provider', title: 'Provedor', field: 'provider', width: '110px', canSort: false },
                    { id: 'documents', title: 'Documentos', field: 'documents', width: '110px', canSort: false },
                    { id: 'createdAt', title: 'Criado em', field: 'createdAt', width: '130px', canSort: false },
                    { id: 'status', title: 'Status', field: 'status', width: '110px', canSort: false },
                    { id: 'actions', title: 'Visualizar', field: 'actions', width: '100px', canSort: false },
                ]}
                onGotApiPayload={(data) => {
                    providers.value = data.providers || [];
                    // Sobrescrever apiVersion para mostrar Recommendation Engine
                    data.apiName = 'Recommendation Engine';
                    data.apiVersion = '1.0.0';
                }}
            >
                {{
                    controlSlot: <div></div>,
                    filterSlot: (
                        <div className="grid grid-cols-12 gap-1 h-[46px]">
                            <SelectFilter
                                id="slc-provider"
                                label="Provedor..."
                                value={provider}
                                onChange={providerFilterChanged}
                                defaultVoidOption
                                defaultLabel="Todos os provedores"
                                options={[
                                    { value: 'QUOD-PJ', label: 'QUOD-PJ' },
                                    { value: 'ESCAVADOR', label: 'ESCAVADOR' },
                                    { value: 'INOVAMIND', label: 'INOVAMIND' },
                                    { value: 'SERASA-PJ', label: 'SERASA-PJ' },
                                    { value: 'SCR', label: 'SCR' },
                                ]}
                                className="col-span-3"
                            />
                            <SelectFilter
                                id="slc-status"
                                label="Status..."
                                value={status}
                                onChange={statusFilterChanged}
                                defaultVoidOption
                                defaultLabel="Todos os status"
                                options={[
                                    { value: 'PENDING', label: 'PENDING' },
                                    { value: 'COMPLETED', label: 'COMPLETED' },
                                ]}
                                className="col-span-2"
                            />
                            <PageSearch
                                value={identification}
                                label="Buscar por identificação..."
                                onSearch={identificationFilterChanged}
                                filterCleanVisible={true}
                                filterCleanEnabled={Boolean(identification || provider || status)}
                                onFilterClean={clearFilters}
                                configureVisible={false}
                                className="col-span-7"
                            />
                        </div>
                    ),
                }}
            </PageList>
        </Fragment>
    );
};

export default RecommendationEngineRecommendationsList;
